import { useEffect, useMemo, useState } from "react";
import { useIsMounted } from "@ct-react/core";
import { ArticleType } from "@shared/url";
import {
  AnnualAccommodationArticleFilterInput, AnyArticleSortInput,
  RentalAccommodationArticleFilterInput,
  SaleAccommodationArticleFilterInput,
  SeasonalAccommodationArticleFilterInput
} from "@shared/graphql";
import {
  AnyArticleSearch,
  CustomRentalAccommodationFilterInput,
  CustomSeasonalAccommodationFilterInput
} from "../contexts/search";
import { PaginationPayload } from "../models/search";
import * as criteriaTools from "../tools/search-params/search-criteria";
import * as paginationTools from "../tools/search-params/search-pagination";

export const formatWhere = (search: AnyArticleSearch) => {
  const { kind, ...criteria } = search;
  switch (kind) {
    case ArticleType.RENTAL:
      const { guests: rentalGuests, ...rentalRest } = criteria as CustomRentalAccommodationFilterInput;
      return {
        rentalAccommodation: {
          ...rentalRest,
          ...!!rentalGuests && { guests: Object.values(rentalGuests).reduce((acc, val) => acc + val, 0) }
        } as RentalAccommodationArticleFilterInput
      };
    case ArticleType.SEASONAL:
      const { guests: seasonalGuests, ...seasonalRest } = criteria as CustomSeasonalAccommodationFilterInput;
      return {
        seasonalAccommodation: {
          ...seasonalRest,
          ...!!seasonalGuests && { guests: Object.values(seasonalGuests).reduce((acc, val) => acc + val, 0) }
        } as SeasonalAccommodationArticleFilterInput
      };
    case ArticleType.ANNUAL:
      return { annualAccommodation: criteria as AnnualAccommodationArticleFilterInput };
    case ArticleType.SALE:
      return { saleAccommodation: criteria as SaleAccommodationArticleFilterInput };
  }
}

export const formatSort = (type: ArticleType, sort?: AnyArticleSortInput[]) => {
  if (!sort || sort.length === 0)
    return undefined;

  switch (type) {
    case ArticleType.RENTAL:
      return { rentalAccommodation: sort as RentalAccommodationArticleFilterInput[] };
    case ArticleType.SEASONAL:
      return { seasonalAccommodation: sort as SeasonalAccommodationArticleFilterInput[] };
    case ArticleType.ANNUAL:
      return { annualAccommodation: sort as AnnualAccommodationArticleFilterInput[] };
    case ArticleType.SALE:
      return { saleAccommodation: sort as SaleAccommodationArticleFilterInput[] };
  }
}

export const useGQLSearchFormatter = (pageSize: number, type: ArticleType, searchParams: URLSearchParams) => {

  const isMounted = useIsMounted();
  const [ criteria, setCriteria ] = useState<AnyArticleSearch>(criteriaTools.parse(searchParams, type));
  const [ pagination, setPagination ] = useState<PaginationPayload>(paginationTools.parse(searchParams));

  useEffect(() => {
    if (!isMounted) return;
    setCriteria(criteriaTools.parse(searchParams, type));
    setPagination(paginationTools.parse(searchParams));
  }, [ type, searchParams ]);

  const where = useMemo(() => formatWhere(criteria), [ criteria ]);

  const others = useMemo(() => ({
    ...(pagination.direction === "after") && {
      first: pageSize,
      ...(!!pagination.cursor) && { after: pagination.cursor },
    },
    ...(pagination.direction === "before") && {
      last: pageSize,
      ...(!!pagination.cursor) && { before: pagination.cursor },
    }
  }), [ pagination ]);

  return { where, ...others };

}
